'use client';

import useSWR from 'swr';
import dayjs from 'dayjs';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { isDateWithinMonthsFromNow } from '@/utils/dates/isDateWithinMonthsFromNow';
import { getLocaleInfo } from '@/utils/helpers';
import { PaymentOption } from './usePaymentOptions';
import { StandaloneAddOn, StartingKit } from './useStartingKit';
import { Media, SubscriptionAddOn } from './useAddOns';
import useLocale from './useLocale';
import {
  ChildInfo,
  postFetcher,
} from '@/components/subflow-v3/context/SubFlowV3Context';
import {
  getUpsellAddOns,
  QR_DATA_KEY,
} from '@/components/subflow-v3/components/utils';
import { isChildInfoCorrect } from '@/components/homepage-us/utils';

export interface OrderReview {
  starting_kit: ReviewStartingKit;
  standalone_add_ons: PreviousPlay[];
  previous_plays: PreviousPlay[];
  upcoming_kits: UpcomingKit[];
  title: string;
  cta_text: string;
  next_page: string;
}

export interface PreviousPlay {
  anchor: string;
  name: string;
  price: number;
  image_uri: string;
  age_band?: string;
  variant_id: string;
  description_html?: string;
}

export interface ReviewStartingKit {
  anchor: string;
  name: string;
  age_band: string;
  image_uri: string;
  discounted_price: number;
  price: number;
  scheduled_date: string;
  payment_option_name: string;
  shopify_variant_id: string;
  add_ons: SubscriptionAddOn[];
}

export interface PurpleComponent {
  id: string;
  uuid: string;
  kind: string;
  anchor: string;
  parent_product_anchor: null;
  parent_product_uuid: null;
  copy_text: CopyText;
  metadata: PurpleMetadata;
  customizable_options: any[];
  language: string;
  media: Media[];
}

export interface CopyText {
  title: string;
  description_html: string;
  skip_copy: null;
  title_tag: null;
  age_tagline: null;
  parent_name: null;
  pricing_copy: null;
  long_description: null;
  rich_snippets_html: null;
}

export enum Key {
  Gallery = 'gallery',
  Primary = 'primary',
}

export enum Kind {
  Image = 'image',
}

export interface PurpleMetadata {
  hidden: string;
}

export interface AddOnMetadata {
  hidden: string;
  max_age: number;
  min_age: number;
  age_unit: string;
  price_cents: number;
  // Optional field added for BFCM 2024
  discounted_price?: number;
  description_tag?: string;
}

export interface Variant {
  id: string;
  uuid: string;
  external_id: string;
  is_default: boolean;
}

export interface UpcomingKit {
  anchor: string;
  name: string;
  age_band: string;
  image_uri: string;
  discounted_price: number;
  price: number;
  scheduled_date: string;
  variant_id: string;
  add_ons: UpcomingKitAddOn[];
}

export interface UpcomingKitAddOn {
  id: string;
  uuid: string;
  kind: string;
  anchor: string;
  parent_product_anchor: string;
  parent_product_uuid: string;
  copy_text: CopyText;
  metadata: AddOnMetadata;
  customizable_options: any[];
  add_ons: any[];
  components: FluffyComponent[];
  related_products: any[];
  variants: Variant[];
  language: string;
  media: Media[];
}

export interface FluffyComponent {
  id: string;
  uuid: string;
  kind: string;
  anchor: string;
  parent_product_anchor: null;
  parent_product_uuid: null;
  copy_text: CopyText;
  metadata: object;
  customizable_options: any[];
  language: string;
  media: Media[];
}

export const useOrderReview = (
  childInfo: Partial<ChildInfo>,
  language: string,
  paymentOption?: PaymentOption,
  startingKit?: StartingKit,
  previousPlayAddOns?: PreviousPlay[],
  subscriptionAddOns?: SubscriptionAddOn[],
  standaloneAddOns?: StandaloneAddOn[],
) => {
  const locale = useLocale();
  const { subflow_v2_enable_standalone_upsell } = useFlags();
  const localeInfo = getLocaleInfo(locale);

  const selected_add_ons =
    subflow_v2_enable_standalone_upsell && startingKit
      ? getUpsellAddOns(startingKit?.anchor, locale)
      : standaloneAddOns?.map((a) => a.anchor);

  const getFormattedDob = () => {
    if (typeof window !== 'undefined') {
      const storage = JSON.parse(
        window.localStorage.getItem(QR_DATA_KEY) || '{}',
      );

      const defaultDob = `${childInfo.year}-${childInfo.month}-${childInfo.day}`;

      if (!storage || !storage.lookerInTargetQR) {
        return defaultDob;
      }

      if (
        isDateWithinMonthsFromNow({
          year: Number(childInfo.year),
          month: Number(childInfo.month),
          day: Number(childInfo.day),
          months: 3,
        })
      ) {
        return dayjs().subtract(3, 'month').format('YYYY-MM-DD');
      }

      return defaultDob;
    }

    return `${childInfo.year}-${childInfo.month}-${childInfo.day}`;
  };

  const body = {
    child_name: childInfo.name,
    date_of_birth: getFormattedDob(),
    payment_option_uuid: paymentOption?.uuid,
    selected_language: language,
    currency_code: localeInfo.currencyCode?.toLowerCase(),
    starting_kit: startingKit?.anchor,
    selected_prev_play: previousPlayAddOns?.map((a) => a.anchor),
    selected_subscription_add_ons: subscriptionAddOns?.map((a) =>
      a.kind.replace(/_addon$/g, ''),
    ),
    selected_add_ons,
    language,
  };

  const hasFullPayload =
    isChildInfoCorrect(childInfo) &&
    paymentOption?.uuid &&
    language &&
    startingKit?.anchor;

  return useSWR<OrderReview>(
    hasFullPayload
      ? [`/api/v1/orchestration_subflow/order_review`, JSON.stringify(body)]
      : null,
    postFetcher,
    { revalidateOnFocus: false },
  );
};
